import type { ZodError, z } from 'zod';
import {
  responseButtonConfigSchema,
  responseButtonModeSchema,
} from '@coaching/api/trpc/routers/bots.response-buttons.schemas.js';

export type ResponseButtonConfig = z.infer<typeof responseButtonConfigSchema>;
export type ResponseButtonMode = z.infer<typeof responseButtonModeSchema>;
export type ResponseButtonSurfaceKey = 'dailyCheckIn' | 'weeklySummary' | 'chatResponses';
export type ResponseButtonSurfaceConfig = NonNullable<ResponseButtonConfig[ResponseButtonSurfaceKey]>;
export type ResponseButtonOption = NonNullable<ResponseButtonSurfaceConfig['staticButtons']>[number];
export type ResponseButtonFallbackMessageMapping = NonNullable<ResponseButtonSurfaceConfig['fallbackMessageMapping']>;
type UnknownRecord = Record<string, unknown>;

export const RESPONSE_BUTTON_MODES = responseButtonModeSchema.options;
export const RESPONSE_BUTTON_ROLLOUT_GUIDANCE = 'Start with static buttons on daily check-ins, then weekly summaries. Leave chat buttons off until the static surfaces feel useful, then test AI chat buttons last and disable any surface if it starts adding noise.';

export const RESPONSE_BUTTON_SURFACES: Array<{
  key: ResponseButtonSurfaceKey;
  label: string;
  description: string;
}> = [
  {
    key: 'dailyCheckIn',
    label: 'Daily check-in',
    description: 'Buttons added to automated daily outreach. Recommended first rollout surface.',
  },
  {
    key: 'weeklySummary',
    label: 'Weekly summary',
    description: 'Buttons added to weekly recap messages after daily static buttons are stable.',
  },
  {
    key: 'chatResponses',
    label: 'Chat responses',
    description: 'Optional AI-suggested buttons alongside conversational replies. Roll this out last.',
  },
];

const RESPONSE_BUTTON_SURFACE_ROLLOUT_HINTS: Record<ResponseButtonSurfaceKey, string> = {
  dailyCheckIn: 'Recommended rollout: begin with static buttons here so operators can learn the useful taps before adding AI-driven suggestions elsewhere.',
  weeklySummary: 'Recommended rollout: add static buttons here after daily check-ins are stable so operators can validate the next higher-volume recap surface before testing chat buttons.',
  chatResponses: 'Recommended rollout: keep this surface disabled by default, then trial AI mode on a small cohort once daily and weekly static buttons are stable.',
};

export interface ResponseButtonConfigHydrationResult {
  success: boolean;
  config: ResponseButtonConfig | null;
  error: string | null;
}

interface NormalizeResponseButtonConfigOptions {
  preserveDefaultConfig?: boolean;
}

export type ResponseButtonConfigSaveResult =
  | { success: true; config: ResponseButtonConfig | undefined }
  | { success: false; error: string };

export function getResponseButtonSurfaceRolloutHint(surfaceKey: ResponseButtonSurfaceKey): string {
  return RESPONSE_BUTTON_SURFACE_ROLLOUT_HINTS[surfaceKey];
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getSurfaceExtras(value: unknown): UnknownRecord {
  if (!isRecord(value)) {
    return {};
  }

  const {
    enabled: _enabled,
    mode: _mode,
    staticButtons: _staticButtons,
    aiPromptOverride: _aiPromptOverride,
    maxButtons: _maxButtons,
    fallbackMessageMapping: _fallbackMessageMapping,
    ...extras
  } = value;

  return extras;
}

function getButtonExtras(value: unknown): UnknownRecord {
  if (!isRecord(value)) {
    return {};
  }

  const {
    id: _id,
    label: _label,
    ...extras
  } = value;

  return extras;
}

function getConfigExtras(value: unknown): UnknownRecord {
  if (!isRecord(value)) {
    return {};
  }

  const {
    enabled: _enabled,
    dailyCheckIn: _dailyCheckIn,
    weeklySummary: _weeklySummary,
    chatResponses: _chatResponses,
    ...extras
  } = value;

  return extras;
}

function hasUnknownRecordEntries(value: UnknownRecord): boolean {
  return Object.keys(value).some((key) => value[key] !== undefined);
}

export function formatResponseButtonConfigIssues(
  issues: Array<{ path: Array<string | number>; message: string }>
): string {
  return issues
    .map((issue) => {
      const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
      return `${path}${issue.message}`;
    })
    .join('\n');
}

function formatHydrationError(error: ZodError): string {
  return formatResponseButtonConfigIssues(error.issues);
}

function getHydratedSurfaceEnabled(
  surfaceKey: ResponseButtonSurfaceKey,
  rawSurface: unknown,
  parsedSurface: ResponseButtonConfig[ResponseButtonSurfaceKey] | undefined
): boolean {
  const parsedEnabled = parsedSurface?.enabled;
  if (parsedEnabled !== undefined) {
    return parsedEnabled;
  }

  if (surfaceKey === 'chatResponses') {
    return false;
  }

  if (isRecord(rawSurface)) {
    return true;
  }

  return false;
}

function buildHydratedSurfaceConfig(
  surfaceKey: ResponseButtonSurfaceKey,
  rawSurface: unknown,
  parsedSurface: ResponseButtonConfig[ResponseButtonSurfaceKey] | undefined
): ResponseButtonSurfaceConfig {
  const rawStaticButtons = isRecord(rawSurface) && Array.isArray(rawSurface.staticButtons)
    ? rawSurface.staticButtons
    : undefined;
  const parsedStaticButtons = parsedSurface?.staticButtons?.map((button, index) => ({
    ...getButtonExtras(rawStaticButtons?.[index]),
    ...button,
  }));

  return {
    ...getSurfaceExtras(rawSurface),
    ...createDefaultResponseButtonSurfaceConfig(surfaceKey),
    ...parsedSurface,
    enabled: getHydratedSurfaceEnabled(surfaceKey, rawSurface, parsedSurface),
    ...(parsedStaticButtons ? { staticButtons: parsedStaticButtons } : {}),
  };
}

export function createDefaultResponseButtonSurfaceConfig(
  surfaceKey?: ResponseButtonSurfaceKey
): ResponseButtonSurfaceConfig {
  return {
    enabled: false,
    mode: surfaceKey === 'chatResponses' ? 'ai' : 'static',
    maxButtons: 3,
  };
}

export function createEmptyResponseButtonConfig(): ResponseButtonConfig {
  return {
    enabled: false,
    dailyCheckIn: createDefaultResponseButtonSurfaceConfig('dailyCheckIn'),
    weeklySummary: createDefaultResponseButtonSurfaceConfig('weeklySummary'),
    chatResponses: createDefaultResponseButtonSurfaceConfig('chatResponses'),
  };
}

export function tryHydrateResponseButtonConfig(value: unknown): ResponseButtonConfigHydrationResult {
  if (value == null) {
    return {
      success: true,
      config: null,
      error: null,
    };
  }

  const parsed = responseButtonConfigSchema.safeParse(value);

  if (!parsed.success) {
    return {
      success: false,
      config: null,
      error: formatHydrationError(parsed.error),
    };
  }

  return {
    success: true,
    error: null,
    config: {
      ...getConfigExtras(value),
      ...createEmptyResponseButtonConfig(),
      ...parsed.data,
      dailyCheckIn: buildHydratedSurfaceConfig(
        'dailyCheckIn',
        isRecord(value) ? value.dailyCheckIn : undefined,
        parsed.data.dailyCheckIn,
      ),
      weeklySummary: buildHydratedSurfaceConfig(
        'weeklySummary',
        isRecord(value) ? value.weeklySummary : undefined,
        parsed.data.weeklySummary,
      ),
      chatResponses: buildHydratedSurfaceConfig(
        'chatResponses',
        isRecord(value) ? value.chatResponses : undefined,
        parsed.data.chatResponses,
      ),
    },
  };
}

function sanitizeButton(button: Partial<ResponseButtonOption>): ResponseButtonOption | null {
  const id = typeof button.id === 'string' ? button.id.trim() : '';
  const label = typeof button.label === 'string' ? button.label.trim() : '';

  if (!id && !label) {
    return null;
  }

  return {
    ...getButtonExtras(button),
    id,
    label,
  };
}

function sanitizeSurfaceConfig(
  surface: ResponseButtonSurfaceConfig,
  rawSurface: unknown,
): ResponseButtonSurfaceConfig {
  const staticButtons = surface.staticButtons
    ?.map((button) => sanitizeButton(button))
    .filter((button): button is ResponseButtonOption => button !== null);
  const aiPromptOverride = surface.aiPromptOverride?.trim();
  const maxButtons = typeof surface.maxButtons === 'number'
    ? Math.max(1, Math.min(3, Math.trunc(surface.maxButtons)))
    : undefined;
  const fallbackMessageMapping = isRecord(surface.fallbackMessageMapping)
    ? Object.fromEntries(
        (staticButtons ?? [])
          .map((button) => {
            const rawMessage = surface.fallbackMessageMapping?.[button.id];
            return [button.id, typeof rawMessage === 'string' ? rawMessage.trim() : ''];
          })
      )
    : undefined;
  const hasFallbackMessageMapping = fallbackMessageMapping != null
    && Object.values(fallbackMessageMapping).some((message) => message.length > 0);

  return {
    ...getSurfaceExtras(rawSurface),
    enabled: surface.enabled ?? false,
    mode: surface.mode,
    ...(staticButtons && staticButtons.length > 0 ? { staticButtons } : {}),
    ...(aiPromptOverride ? { aiPromptOverride } : {}),
    ...(maxButtons ? { maxButtons } : {}),
    ...(hasFallbackMessageMapping ? { fallbackMessageMapping } : {}),
  };
}

function isDefaultSurfaceConfig(
  surfaceKey: ResponseButtonSurfaceKey,
  surface: ResponseButtonSurfaceConfig
): boolean {
  return (
    surface.enabled === false
    && surface.mode === createDefaultResponseButtonSurfaceConfig(surfaceKey).mode
    && surface.maxButtons === 3
    && !surface.staticButtons?.length
    && !(surface.aiPromptOverride && surface.aiPromptOverride.trim())
    && !surface.fallbackMessageMapping
  );
}

function hasSurfaceContent(
  surfaceKey: ResponseButtonSurfaceKey,
  surface: ResponseButtonSurfaceConfig
): boolean {
  if (isDefaultSurfaceConfig(surfaceKey, surface)) {
    return false;
  }

  return Boolean(
    surface.enabled
    || (surface.staticButtons && surface.staticButtons.length > 0)
    || (surface.aiPromptOverride && surface.aiPromptOverride.trim())
    || surface.mode !== 'static'
    || (typeof surface.maxButtons === 'number' && surface.maxButtons !== 3)
  );
}

export function hasResponseButtonConfigContent(config: ResponseButtonConfig): boolean {
  return Boolean(
    config.enabled
    || (config.dailyCheckIn && hasSurfaceContent('dailyCheckIn', config.dailyCheckIn))
    || (config.weeklySummary && hasSurfaceContent('weeklySummary', config.weeklySummary))
    || (config.chatResponses && hasSurfaceContent('chatResponses', config.chatResponses))
  );
}

function hasUnknownResponseButtonConfigContent(config: ResponseButtonConfig): boolean {
  if (hasUnknownRecordEntries(getConfigExtras(config))) {
    return true;
  }

  return RESPONSE_BUTTON_SURFACES.some((surfaceMeta) => {
    const surface = config[surfaceMeta.key];
    return Boolean(
      surface
      && (
        hasUnknownRecordEntries(getSurfaceExtras(surface))
        || surface.staticButtons?.some((button) => hasUnknownRecordEntries(getButtonExtras(button)))
      )
    );
  });
}

export function hasUnknownResponseButtonConfigFields(config: ResponseButtonConfig): boolean {
  return hasUnknownResponseButtonConfigContent(config);
}

export function normalizeResponseButtonConfig(
  config: ResponseButtonConfig,
  options: NormalizeResponseButtonConfigOptions = {},
): ResponseButtonConfig | undefined {
  const rawConfig: UnknownRecord & Partial<Record<ResponseButtonSurfaceKey, unknown>> = isRecord(config)
    ? config
    : {};
  const normalized: ResponseButtonConfig = {
    ...getConfigExtras(rawConfig),
    enabled: config.enabled,
      dailyCheckIn: sanitizeSurfaceConfig(
      config.dailyCheckIn ?? createDefaultResponseButtonSurfaceConfig('dailyCheckIn'),
      rawConfig.dailyCheckIn,
    ),
    weeklySummary: sanitizeSurfaceConfig(
      config.weeklySummary ?? createDefaultResponseButtonSurfaceConfig('weeklySummary'),
      rawConfig.weeklySummary,
    ),
    chatResponses: sanitizeSurfaceConfig(
      config.chatResponses ?? createDefaultResponseButtonSurfaceConfig('chatResponses'),
      rawConfig.chatResponses,
    ),
  };

  if (
    !options.preserveDefaultConfig
    && !hasResponseButtonConfigContent(normalized)
    && !hasUnknownResponseButtonConfigContent(normalized)
  ) {
    return undefined;
  }

  return normalized;
}

export function prepareResponseButtonConfigForSave(
  config: ResponseButtonConfig,
  options: NormalizeResponseButtonConfigOptions = {},
): ResponseButtonConfigSaveResult {
  const normalizedConfig = normalizeResponseButtonConfig(config, options);
  if (!normalizedConfig) {
    return {
      success: true,
      config: undefined,
    };
  }

  const validationResult = responseButtonConfigSchema.safeParse(normalizedConfig);
  if (!validationResult.success) {
    return {
      success: false,
      error: formatResponseButtonConfigIssues(validationResult.error.issues),
    };
  }

  return {
    success: true,
    config: {
      ...getConfigExtras(normalizedConfig),
      ...validationResult.data,
      dailyCheckIn: validationResult.data.dailyCheckIn
        ? buildHydratedSurfaceConfig('dailyCheckIn', normalizedConfig.dailyCheckIn, validationResult.data.dailyCheckIn)
        : undefined,
      weeklySummary: validationResult.data.weeklySummary
        ? buildHydratedSurfaceConfig('weeklySummary', normalizedConfig.weeklySummary, validationResult.data.weeklySummary)
        : undefined,
      chatResponses: validationResult.data.chatResponses
        ? buildHydratedSurfaceConfig('chatResponses', normalizedConfig.chatResponses, validationResult.data.chatResponses)
        : undefined,
    },
  };
}
